<template>
  <b-modal
    id="share-externally-modal"
    title="Share Externally"
    size="lg"
    :visible="isVisible"
    @hidden="closeModal"
    @ok="closeModal"
    @cancel="closeModal"
    ok-title="Close"
    cancel-title=""
    :hide-footer="true"
    :hide-header-close="false"
  >
    <div class="share-externally-content">
      <!-- Loading State -->
      <div v-if="isLoadingSharedUsers" class="text-center">
        <b-spinner class="m-2" variant="itg-dark" role="status"></b-spinner>
        <p>Loading shared users...</p>
      </div>

      <!-- All other content should be inside v-else -->
      <div v-else>
        <!-- No Shared Users -->
        <div v-if="!sharedUsers || sharedUsers.length === 0" class="text-center mb-4">
          <i class="fa fa-users fa-3x text-muted mb-3"></i>
          <h5>No External Users</h5>
          <p class="text-muted">This ticket is not currently shared with any external users.</p>
        </div>

        <!-- No External Users Available -->
        <div v-else-if="!isLoadingExternalUsers && availableExternalUsers.length === 0" class="text-center mb-4">
          <i class="fa fa-user-plus fa-3x text-muted mb-3"></i>
          <h5>No External Users Available</h5>
          <p class="text-muted">There are no external users available to share this ticket with.</p>
        </div>

        <!-- Shared Users List -->
        <div v-if="sharedUsers && sharedUsers.length > 0">
          <div class="shared-users-header mb-3">
            <h6 class="mb-2">Currently Shared With ({{ sharedUsers.length }})</h6>
            <p class="text-muted small">These users have access to view this ticket and its incident report.</p>
          </div>

          <div class="shared-users-list">
            <div 
              v-for="sharedUser in sharedUsers" 
              :key="sharedUser.id" 
              class="shared-user-item d-flex justify-content-between align-items-center"
            >
              <div class="user-info d-flex align-items-center">
                <div class="user-avatar">
                  <span class="avatar-title rounded bg-soft-primary text-primary">
                    {{ sharedUser.user.name.charAt(0).toUpperCase() }}
                  </span>
                </div>
                <div class="user-details">
                  <div class="user-name">{{ sharedUser.user.name }}</div>
                  <div class="user-email text-muted">{{ sharedUser.user.email }}</div>
                  <div class="shared-date text-muted small">
                    Shared since {{ formatDate(sharedUser.created_at) }}
                  </div>
                </div>
              </div>
              <button 
                class="btn btn-outline-danger btn-sm ml-3"
                @click="confirmUnshare(sharedUser.user_id, sharedUser.user.name)"
              >
                Unshare
              </button>
            </div>
          </div>
        </div>

        <!-- Add User Form - Always at the bottom when there are external users available -->
        <div v-if="availableExternalUsers.length > 0" class="add-user-section mt-4">
          <hr class="my-4">
          <div class="add-user-form">
            <div class="card">
              <div class="card-body">
                <div class="form-group">
                  <label>Share With External User</label>
                  <select 
                    v-model="selectedUserId" 
                    class="form-control"
                    :disabled="isLoadingExternalUsers || isSharing"
                  >
                    <option value="">Choose an external user...</option>
                    <option 
                      v-for="user in filteredExternalUsers" 
                      :key="user.id" 
                      :value="user.id"
                    >
                      {{ user.name }} ({{ user.email }})
                    </option>
                  </select>
                </div>
                <div class="d-flex justify-content-end mt-3">
                  <button 
                    @click="shareTicket" 
                    class="btn btn-itg-dark"
                    :disabled="!selectedUserId || isSharing"
                  >
                    <span v-if="isSharing">
                      <i class="fa fa-spinner fa-spin mr-1"></i>
                      Sharing...
                    </span>
                    <span v-else>
                      Share Ticket
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Dates from "../../../../../../mixins/Dates";
import axios from 'axios';

export default {
  name: "ShareExternallyModal",
  mixins: [Dates],
  data() {
    return {
      isVisible: false,
      selectedUserId: '',
      availableExternalUsers: [],
      isLoadingExternalUsers: false,
      isSharing: false
    };
  },
  computed: {
    ...mapGetters('ticketViewer', {
      sharedUsers: 'sharedUsers',
      isLoadingSharedUsers: 'isLoadingSharedUsers',
      ticket: 'ticket'
    }),
    filteredExternalUsers() {
      if (!this.availableExternalUsers) return [];
      const sharedIds = (this.sharedUsers || []).map(u => u.user_id);
      return this.availableExternalUsers.filter(u => !sharedIds.includes(u.id));
    }
  },
  methods: {
    ...mapActions('ticketViewer', {
      loadSharedUsers: 'loadSharedUsers'
    }),

    openModal() {
      this.isVisible = true;
      this.$bvModal.show('share-externally-modal');
      this.loadExternalUsers();
    },

    closeModal() {
      this.isVisible = false;
      this.$bvModal.hide('share-externally-modal');
      this.resetForm();
    },

    formatDate(dateString) {
      return this.displayTimeAgo(dateString);
    },

    loadExternalUsers() {
      this.isLoadingExternalUsers = true;
      axios.get(API_BASE + '/users/external').then(response => {
        this.availableExternalUsers = response.data;
        this.isLoadingExternalUsers = false;
      }).catch(error => {
        console.error('Error loading external users:', error);
        this.isLoadingExternalUsers = false;
        this.$bvToast.toast('Failed to load external users', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      });
    },

    shareTicket() {
      if (!this.selectedUserId || !this.ticket) {
        return;
      }

      this.isSharing = true;
      axios.post(API_BASE + `/tickets/${this.ticket.id}/share`, {
        user_id: this.selectedUserId
      }).then(response => {
        this.isSharing = false;
        this.resetForm();
        this.loadSharedUsers(); // Reload the shared users list
        
        this.$bvToast.toast('Ticket shared successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      }).catch(error => {
        this.isSharing = false;
        console.error('Error sharing ticket:', error);
        
        let errorMessage = 'Failed to share ticket';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      });
    },

    resetForm() {
      this.selectedUserId = '';
    },

    confirmUnshare(userId, userName) {
      this.$bvModal.msgBoxConfirm(
        `Are you sure you want to unshare this ticket with ${userName}?`,
        {
          title: 'Confirm Unshare',
          size: 'md',
          okVariant: 'danger',
          okTitle: 'Unshare',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          centered: true
        }
      ).then(confirmed => {
        if (confirmed) {
          this.unshareUser(userId);
        }
      });
    },

    unshareUser(userId) {
      if (!this.ticket) return;
      this.isSharing = true;
      axios.post(API_BASE + `/tickets/${this.ticket.id}/unshare`, { user_id: userId })
        .then(() => {
          this.isSharing = false;
          this.loadSharedUsers();
          this.$bvToast.toast('User unshared successfully', {
            title: 'Success',
            variant: 'success',
            solid: true
          });
        })
        .catch(error => {
          this.isSharing = false;
          let errorMessage = 'Failed to unshare user';
          if (error.response && error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          }
          this.$bvToast.toast(errorMessage, {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
        });
    }
  }
};
</script>

<style scoped lang="scss">
.share-externally-content {
  min-height: 200px;
}

.shared-users-header {
  // border-bottom: 1px solid #e9ecef;
  // padding-bottom: 15px;
}

.shared-users-list {
  max-height: 400px;
  overflow-y: auto;
}

.shared-user-item {
  padding: 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 10px;
  background: #f8f9fa;

  &:last-child {
    margin-bottom: 0;
  }
}

.user-info {
  display: flex;
  align-items: flex-start;
}

.user-avatar {
  margin-right: 15px;
  flex-shrink: 0;
}

.avatar-title {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
}

.user-details {
  flex: 1;
}

.user-name {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 2px;
}

.user-email {
  font-size: 13px;
  margin-bottom: 4px;
}

.shared-date {
  font-size: 11px;
}

.add-user-form {
  .card {
    border: 1px solid #e9ecef;
    border-radius: 8px;
  }

  .card-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    padding: 12px 15px;
  }

  .card-body {
    padding: 15px;
  }
}

.cursor-not-allowed {
  cursor: not-allowed;
}
</style> 